import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Chip,
    Stack,
    IconButton,
    Link,
} from "@mui/material";
import { styled } from "styled-components";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useDropzone } from "react-dropzone";
import { v4 as uuidv4 } from "uuid";
import CompRgstrdFormSubmitted from "./Modals/CompRgstrdFormSubmitted";
import axios from "axios";
import BASE_API_URL from "../apis/index";

const dosageOptions = [
    "Tablets",
    "Capsules",
    "Powders",
    "Granules",
    "Sachets",
    "Liquids",
    "Ointments/Gels",
    "Lozenges",
    "Chewables",
    "Gummies",
    "Jellies",
    "Effervescent",
    "Bars",
    "Other",
];

const specialitiesOptions = [
    "Probiotics",
    "Enzymes",
    "Premixes",
    "Dietary Supplements",
    "Functional Foods",
    "Sports Nutrition",
    "Ayurvedic & Herbal",
    "Cosmetic",
    "Hygiene",
    "Veterinary",
];

const capabilitiesOptions = [
    "Custom Formulations",
    "Private Label",
    "White Label",
    "Specialized Technologies",
    "Packaging",
    "Quality Control",
    "Sourcing/Supply Chain",
    "Regulatory",
    "Compliance",
    "Full-Service Manufacturing",
];

const packagingOptions = [
    "Jars",
    "Containers",
    "Bottle",
    "Canisters",
    "Tubes",
    "Pouch",
    "Sachets",
    "Strips",
    "Blisters",
    "Vials",
    "Ampoules",
];

/** ✅ Generic Chip Selector */
const ChipSelector = ({ label, required, options, selected, onToggle }) => (
    <>
        <Typography fontWeight={500} mb={2} mt={3}>
            {label}
            {required && <span className="text-danger">*</span>} :
        </Typography>
        <Stack direction="row" gap={1} flexWrap="wrap" className="dosageTypesField">
            {options.map((option) => (
                <Chip
                    key={option}
                    label={option}
                    variant={selected.includes(option) ? "filled" : "outlined"}
                    color={selected.includes(option) ? "warning" : "default"}
                    icon={selected.includes(option) ? <CheckIcon /> : null}
                    onClick={() => onToggle(option)}
                    size="large"
                />
            ))}
        </Stack>
    </>
);

const CompanyServiceRegistrationForm = ({ compId, initialData, isEdit = false, serviceId }) => {
    const [loading, setLoading] = useState(false);
    const [customDosage, setCustomDosage] = useState("");
    const [existingDocuments, setExistingDocuments] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [showRgstrdScsModal, setShowRgstrdScsModal] = useState(false);

    const [formData, setFormData] = useState(
        initialData || {
            dosageFormats: [],
            specialities: [],
            capabilities: [],
            packaging: [],
            documents: null,
        }
    );

    useEffect(() => {
        if (initialData) {
            setFormData(initialData);
            setExistingDocuments(initialData.existingDocuments || []);
        }
    }, [initialData]);

    // useEffect(() => { console.log(formData) }, [formData])

    /** ✅ Generic toggle */
    const toggleField = (field, option) => {
        setFormData((prev) => ({
            ...prev,
            [field]: prev[field].includes(option)
                ? prev[field].filter((item) => item !== option)
                : [...prev[field], option],
        }));
    };

    // Add custom dosage format
    const addCustomDosage = () => {
        const trimmed = customDosage.trim();
        if (trimmed && !formData.dosageFormats.includes(trimmed)) {
            setFormData((prev) => ({
                ...prev,
                dosageFormats: [...prev.dosageFormats, trimmed],
            }));
            setCustomDosage("");
        }
    };

    /** ✅ File Drop */
    const onDropDocuments = (acceptedFiles) => {
        const docFiles = acceptedFiles.map((file) => ({
            id: uuidv4(),
            file,
            name: "",
        }));
        setDocuments((prev) => [...prev, ...docFiles]);
    };

    const { getRootProps: getDocRootProps, getInputProps: getDocInputProps } = useDropzone({
        accept: { "application/pdf": [] },
        onDrop: onDropDocuments,
    });

    /** ✅ Validation */
    const validateForm = () => {
        if (!formData.dosageFormats.length)
            return "Please select at least one dosage format";
        if (!formData.specialities.length)
            return "Please select at least one speciality";
        if (!formData.capabilities.length)
            return "Please select at least one capability";
        if (!formData.packaging.length)
            return "Please select at least one packaging option";
        return null;
    };

    /** ✅ Submit */
    const handleSubmit = async () => {
        const errorMsg = validateForm();
        if (errorMsg) return alert(errorMsg);

        try {
            setLoading(true);
            const authToken = localStorage.getItem("authToken");

            const submissionData = new FormData();
            submissionData.append("company_id", compId);
            ["dosageFormats", "specialities", "capabilities", "packaging"].forEach((field) => submissionData.append(field, JSON.stringify(formData[field])));

            // 🔹 Existing docs (only URLs + names from DB)
            submissionData.append(
                "existingDocuments",
                JSON.stringify(existingDocuments.map((doc) => doc.file)) // old file URLs
            );
            submissionData.append(
                "existingDocumentNames",
                JSON.stringify(existingDocuments.map((doc) => doc.name))
            );

            // 🔹 New docs (only the ones user uploads this time)
            submissionData.append(
                "documentNames",
                JSON.stringify(documents.map((doc) => doc.name))
            );
            documents.forEach((doc) =>
                doc.file instanceof File &&
                submissionData.append("supportingDocuments", doc.file)
            );

            let res;
            if (isEdit) {
                // submissionData.append("service_id", serviceId);
                res = await axios.put(
                    `${BASE_API_URL}/api/seller/service/update/${serviceId}`,
                    submissionData,
                    { headers: { Authorization: `Bearer ${authToken}` } }
                );
            } else {
                res = await axios.post(
                    `${BASE_API_URL}/api/seller/service/register/${compId}`,
                    submissionData,
                    { headers: { Authorization: `Bearer ${authToken}` } }
                );
            }

            if (res.data.status === "ok") setShowRgstrdScsModal(true);
        } catch (err) {
            console.error(err);
            alert("Error submitting service.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Wrapper>
            <Box>
                {/* ✅ Dropzone */}
                <Box className="fileDropBox" {...getDocRootProps()}>
                    <input {...getDocInputProps()} />
                    <Typography>Upload Necessary PDF documents</Typography>
                </Box>

                {/* ✅ Existing Docs and names */}
                <Box mt={2}>
                    {existingDocuments.map((doc) => (
                        <Stack key={doc.id} direction="row" spacing={2} alignItems="center" my={1}>
                            <Link href={`${BASE_API_URL}/uploads/user_srvc_docs/${doc.file}`} target="_blank">
                                <button className="viewDocBtn"> View Document </button>
                            </Link>
                            <TextField
                                size="small"
                                label="Document Name"
                                value={doc.name}
                                onChange={(e) =>
                                    setExistingDocuments((prev) =>
                                        prev.map((d) =>
                                            d.id === doc.id ? { ...d, name: e.target.value } : d
                                        )
                                    )
                                }
                            />
                            <IconButton color="error" onClick={() => setExistingDocuments((prev) => prev.filter((d) => d.id !== doc.id))}>
                                <CloseIcon />
                            </IconButton>
                        </Stack>
                    ))}
                </Box>

                <Box mt={2}>
                    {documents.map((doc) => (
                        <Stack
                            key={doc.id}
                            direction="row"
                            spacing={2}
                            alignItems="center"
                            my={1}
                        >
                            <Typography>{doc.file.name}</Typography>
                            <TextField
                                size="small"
                                label="Document Name"
                                value={doc.name}
                                onChange={(e) =>
                                    setDocuments((prev) =>
                                        prev.map((d) =>
                                            d.id === doc.id ? { ...d, name: e.target.value } : d
                                        )
                                    )
                                }
                            />
                            <IconButton
                                color="error"
                                onClick={() =>
                                    setDocuments((prev) => prev.filter((d) => d.id !== doc.id))
                                }
                            >
                                <CloseIcon />
                            </IconButton>
                        </Stack>
                    ))}
                </Box>

                {/* ✅ Dosage Formats */}
                <ChipSelector label="Dosage Formats" required options={dosageOptions} selected={formData.dosageFormats} onToggle={(opt) => toggleField("dosageFormats", opt)} />
                {/* ✅ Custom Dosage */}
                {formData.dosageFormats.includes("Other") && (
                    <Box mt={3}>
                        <Stack direction="row" gap={1} mb={1}>
                            {formData.dosageFormats
                                .filter(
                                    (opt) => !dosageOptions.includes(opt) && opt !== "Other"
                                )
                                .map((opt) => (
                                    <Chip key={opt} label={opt} variant="filled" color="warning" icon={<CheckIcon />} onDelete={() => toggleField("dosageFormats", opt)} size="large" />
                                ))}
                        </Stack>
                        <div className="d-flex">
                            <TextField
                                label="Enter Custom Dosage"
                                size="small"
                                value={customDosage}
                                onChange={(e) => setCustomDosage(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        addCustomDosage();
                                    }
                                }}
                            />
                            <Button variant="contained" color="warning" size="small" className="shadow-none" onClick={addCustomDosage} > Add </Button>
                        </div>
                    </Box>
                )}

                {/* ✅ Other Sections */}
                <ChipSelector label="Specialities" required options={specialitiesOptions} selected={formData.specialities} onToggle={(opt) => toggleField("specialities", opt)} />
                <ChipSelector label="Capabilities" required options={capabilitiesOptions} selected={formData.capabilities} onToggle={(opt) => toggleField("capabilities", opt)} />
                <ChipSelector label="Packaging" required options={packagingOptions} selected={formData.packaging} onToggle={(opt) => toggleField("packaging", opt)} />
            </Box>

            {/* ✅ Submit */}
            <Button variant="contained" color="warning" onClick={handleSubmit} sx={{ mt: 3 }} className="w-100 py-3 shadow-none" disabled={loading}>
                {loading ? "Please wait!..." : "Submit Product"}
            </Button>

            <CompRgstrdFormSubmitted show={showRgstrdScsModal} onClose={() => setShowRgstrdScsModal(false)} redirectLink={`/user-company-details?compId=${compId}`} />
        </Wrapper>
    );
};

export default CompanyServiceRegistrationForm;

const Wrapper = styled(Box)`
  margin: 50px auto;
  .fileDropBox {
    padding: 20px;
    border: 2px dashed #ccc;
    border-radius: 8px;
    text-align: center;
    background: #f8f8f8;
    cursor: pointer;
  }
  .MuiChip-root {
    cursor: pointer;
    padding: 10px;
    font-size: 14px;
    border-radius: 8px;
  }
  .MuiChip-filled {
    span {
      color: white !important;
    }
    svg {
      fill: white;
    }
  }

  .viewDocBtn{
    border: 1px solid var(--secondary-color);
    padding: 8px 15px;
    border-radius: 4px;
    background: #fffaf4ff;
    &:hover{
        background: #ffe8cbff;
    }
  }
`;
